/**
 * Textos de apoio do cadastro de Telegram (Criar/Editar Usuário).
 *
 * A regra de distribuição de feedback por papel, que a API resolve em /telegram/feedback:
 *   salesman                          -> o feedback do próprio atendimento
 *   supervisor / manager / director   -> os vendedores das lojas em que tem permissão
 *   admin                             -> fora da regra (não recebe)
 *
 * Uma regra só para toda a chefia (08/09/2026). Diretor era "a empresa toda" e passou a valer
 * o mesmo que os outros: o escopo é sempre explícito em core.permissions, sem exceção
 * escondida no código. Quem quiser que um diretor veja tudo, dá permissão em tudo.
 *
 * Quem cadastra escolhe a frequência livremente, sem trava por papel — mas precisa ver o
 * tamanho do que está ligando. Os números são medição real de 30 dias em produção
 * (08/09/2026): a madeiramadeira faz 111 feedbacks/dia no tenant, pico de 244, e ~24,5 por
 * loja. O Telegram aceita ~1 mensagem/s por chat, então "a cada atendimento" para quem tem
 * loja inteira debaixo rende 429 e uma caixa inútil.
 */

/**
 * INTERRUPTOR ÚNICO do campo "Frequência" (core.users.feedback_telegram_mode).
 *
 * Desligado em 08/09/2026 por decisão de produto: enquanto o RESUMO DIÁRIO não tem agendador
 * (falta o workflow de horário no n8n), todo destinatário recebe POR ATENDIMENTO — inclusive
 * gerente, supervisor e diretor. O campo sai da tela para não oferecer uma opção que hoje
 * significaria entrega ZERO.
 *
 * Nada foi removido: o endpoint /telegram/feedback/digest, o SQL de agregação e o modo
 * 'digest' continuam de pé e testados. Para trazer o campo de volta, basta `true` aqui —
 * mas o agendador precisa existir ANTES, senão quem escolher "resumo diário" não recebe nada.
 *
 * O volume disso é conhecido e aceito: madeiramadeira faz 111 feedbacks/dia (pico 244) e o
 * Telegram aceita ~1 msg/s por chat, então diretor de cliente grande vai tomar 429 até o
 * resumo entrar. É o que o aviso de escopo na tela diz.
 */
export const MOSTRAR_FREQUENCIA = false;

/** O que a tela manda enquanto MOSTRAR_FREQUENCIA é false. */
export const MODO_PADRAO = 'each';

/** Papéis que a regra de distribuição alcança (a query de destinatários da API). */
export const PAPEIS_QUE_RECEBEM = ['salesman', 'supervisor', 'manager', 'director'];

/**
 * Por que esta pessoa NÃO vai receber nada, ou null se vai.
 *
 * Existe porque o cadastro deixava configurar um estado que nunca entrega, sem avisar: em
 * 09/09 o Andre Franco estava vinculado, habilitado e com tipo marcado — e `admin` não casa
 * com nenhum ramo da regra, então ele receberia silêncio perfeito, sem erro nem log.
 */
export function telegramNaoRecebe(role, { short, long } = {}) {
    if (role && !PAPEIS_QUE_RECEBEM.includes(role)) {
        // Genérico para papel desconhecido: se um perfil novo for criado no futuro e não
        // entrar na regra da API, a frase continua verdadeira em vez de falar de "admin".
        const quem = role === 'admin' ? 'Administrador' : 'Este perfil';
        return `${quem} não entra na distribuição de feedbacks — a pessoa não recebe nada, `
            + 'mesmo vinculada. Para receber, o perfil precisa ser Vendedor, Supervisor, '
            + 'Gerente ou Diretor.';
    }
    if (short === false && long === false) {
        return 'Sem marcar nenhum tipo, a pessoa não recebe nada.';
    }
    return null;
}

export function telegramEscopoTexto(role) {
    switch (role) {
        case 'salesman':
            return 'Recebe os feedbacks dos atendimentos dele.';
        case 'supervisor':
        case 'manager':
            return 'Recebe os feedbacks dos vendedores das lojas em que está cadastrado.';
        case 'director':
            return 'Recebe os feedbacks dos vendedores das lojas em que está cadastrado.';
        case 'admin':
            return 'Administrador não entra na distribuição de feedbacks.';
        default:
            return '';
    }
}

export function telegramEscopoHint(role, mode) {
    const escopo = telegramEscopoTexto(role);
    if (mode !== 'each') return escopo;

    if (role === 'salesman') {
        return `${escopo} Uma mensagem por atendimento.`;
    }
    if (role === 'admin') {
        return escopo;
    }
    // Aqui é onde "a cada atendimento" machuca: o volume é o das lojas inteiras, não o de
    // uma pessoa. Vale igual para diretor desde que ele virou escopo de permissão.
    return `${escopo} Atenção: "a cada atendimento" manda UMA mensagem por atendimento das `
        + `lojas dele — em cliente grande isso passa de 100 por dia. "Resumo diário" costuma ser o certo.`;
}

export default { telegramEscopoTexto, telegramEscopoHint, telegramNaoRecebe,
                 PAPEIS_QUE_RECEBEM, MOSTRAR_FREQUENCIA, MODO_PADRAO };
